import { defineComponent as _defineComponent } from 'vue'

// el-dropdown is not globally registered (see globalComponents.js); register
// locally like every other consumer. Unaliased imports only.
import { Dropdown, DropdownMenu } from 'element-ui'

const __default__ = {
  name: 'campaigns-list',
  components: {
    [Dropdown.name]: Dropdown,
    [DropdownMenu.name]: DropdownMenu
  }
}

import { ref, computed, onMounted, onBeforeUnmount, getCurrentInstance } from 'vue'
import axios from 'axios'
import swal from 'sweetalert2'
import { BPagination } from 'bootstrap-vue'
import { Auth } from 'aws-amplify'
import { API_LOCATION } from '@/api-config'
import moment from 'moment-timezone'
import { formatGroupDate, formatGroupTime } from '@/plugins/dates'
import { Badge } from 'src/components'
import TabRail from '@/components/TabRail.vue'


export default /*#__PURE__*/_defineComponent({
  ...__default__,
  setup(__props) {

const { proxy } = getCurrentInstance() as any

const companyId = localStorage.getItem('companyId')
const companyGroupId = JSON.parse(localStorage.getItem('group') || '{}').id
const baseUrl = `${API_LOCATION}companies/${companyId}/groups/${companyGroupId}`

const loading = ref(true)
const refreshing = ref(false)
const campaigns = ref<any[]>([])
const totalRows = ref(0)
const currentPage = ref(1)
const perPage = ref(10)
const view = ref('active')
const activeTab = ref(0)
const filterValues = ref<Record<string, any>>({ name: '', status: [] })

const filterSchema = [
  { key: 'name', type: 'text', label: 'Name', placeholder: 'Campaign name', primary: true },
  {
    key: 'status',
    type: 'multiselect',
    label: 'Status',
    primary: true,
    options: [
      { label: 'Draft', value: 'draft' },
      { label: 'Scheduled', value: 'scheduled' },
      { label: 'Sending', value: 'sending' },
      { label: 'Sent', value: 'sent' },
      { label: 'Cancelled', value: 'cancelled' },
      { label: 'Failed', value: 'failed' }
    ]
  }
]

const hasActiveFilters = computed(() =>
  !!(filterValues.value.name || (filterValues.value.status && filterValues.value.status.length)))

const STATUS_COLORS: Record<string, string> = {
  draft: 'muted',
  scheduled: 'info',
  sending: 'warning',
  sent: 'success',
  cancelled: 'muted',
  failed: 'danger'
}
const statusColor = (status: string) => STATUS_COLORS[status] || 'muted'

// Ongoing (daily) campaigns reuse the status enum: scheduled = active,
// sent = finished (past the stop date), cancelled = stopped
function statusLabel(campaign: any): string {
  if (campaign.recurrence !== 'daily') return campaign.status
  if (campaign.status === 'scheduled') return 'active'
  if (campaign.status === 'sent') return 'finished'
  if (campaign.status === 'cancelled') return 'stopped'
  return campaign.status
}

function sendTimeDisplay(campaign: any): string {
  const hm = String(campaign.send_time || '09:00').slice(0, 5)
  const group = JSON.parse(localStorage.getItem('group') || '{}')
  const tz = group.tz_name || 'UTC'
  const m = moment.tz(`2000-01-01 ${hm}`, 'YYYY-MM-DD HH:mm', tz)
  return m.isValid() ? formatGroupTime(m.toISOString()) : hm
}

function formatDateTime(value: string) {
  const date = formatGroupDate(value, { dayOfWeek: false })
  const time = formatGroupTime(value)
  return `${date}, ${time}`
}

async function authConfig() {
  const session = await Auth.currentSession()
  return { headers: { Authorization: (session as any).idToken.jwtToken } }
}

// While any listed campaign is sending, refresh quietly every 10s so the
// status flips to sent without a manual page reload (mirrors the report's
// poll cadence). The interval clears itself once nothing is sending.
let sendingPollTimer: ReturnType<typeof setInterval> | null = null
function syncSendingPoll() {
  const anySending = campaigns.value.some((c: any) => c.status === 'sending')
  if (anySending && !sendingPollTimer) {
    sendingPollTimer = setInterval(() => { getCampaigns(false) }, 10000)
  } else if (!anySending && sendingPollTimer) {
    clearInterval(sendingPollTimer)
    sendingPollTimer = null
  }
}
onBeforeUnmount(() => {
  if (sendingPollTimer) clearInterval(sendingPollTimer)
})

async function getCampaigns(showLoader = true) {
  if (showLoader) loading.value = true
  else refreshing.value = true
  try {
    const config = await authConfig()
    const offset = (currentPage.value - 1) * perPage.value
    let url = `${baseUrl}/email-campaigns?count=${perPage.value}&offset=${offset}`
    if (view.value === 'archived') url += '&archived=1'
    if (filterValues.value.name) url += `&name=${encodeURIComponent(filterValues.value.name)}`
    if (filterValues.value.status && filterValues.value.status.length) {
      url += `&status=${filterValues.value.status.join(',')}`
    }
    const response = await axios.get(url, config)
    campaigns.value = response.data.campaigns || []
    totalRows.value = response.data.total_rows || 0
    syncSendingPoll()
  } catch (err: any) {
    if (err?.response?.status === 401) {
      proxy.$router.push('/forbidden')
      return
    }
    swal('Error!', 'Failed to load campaigns', 'error')
  } finally {
    loading.value = false
    refreshing.value = false
  }
}

function switchTab(index: number) {
  activeTab.value = index
  view.value = index === 1 ? 'archived' : 'active'
  currentPage.value = 1
  getCampaigns()
}

function onFiltersChanged(values: Record<string, any>) {
  filterValues.value = values
  currentPage.value = 1
  getCampaigns(false)
}

function onPerPageChange(value: number) {
  perPage.value = value
  currentPage.value = 1
  getCampaigns(false)
}

// Row click mirrors the POS list: editable campaigns open the editor,
// finished ones open their report
// An ongoing campaign that has started (active) is read only: its report
// shows the runs so far. Only a draft ongoing campaign opens in the editor.
function isEditable(campaign: any): boolean {
  if (campaign.recurrence === 'daily') return campaign.status === 'draft'
  return ['draft', 'scheduled'].includes(campaign.status)
}

function openCampaign(campaign: any) {
  if (isEditable(campaign)) {
    editCampaign(campaign)
  } else {
    viewReport(campaign)
  }
}

function createCampaign() {
  proxy.$router.push('/marketing/campaigns/new')
}

function editCampaign(campaign: any) {
  proxy.$router.push(`/marketing/campaigns/${campaign.id}`)
}

function viewReport(campaign: any) {
  proxy.$router.push(`/marketing/campaigns/${campaign.id}/report`)
}

function copyUtmToken(campaign: any) {
  navigator.clipboard.writeText(`campaign-${campaign.id}`)
  proxy.$notify({ type: 'success', message: 'Copied to clipboard', icon: 'tim-icons icon-check-2' })
}

function duplicateCampaign(campaign: any) {
  proxy.$router.push(`/marketing/campaigns/new?from=${campaign.id}`)
}

function cancelPrompt(campaign: any) {
  const ongoing = campaign.recurrence === 'daily'
  swal({
    title: ongoing ? 'Stop this ongoing campaign?' : 'Cancel this campaign?',
    text: ongoing
      ? 'No more daily sends will go out. Runs already sent keep their reports.'
      : campaign.status === 'sending'
        ? 'Emails already sent cannot be recalled. Remaining recipients will be skipped.'
        : 'The campaign will not send at its scheduled time.',
    type: 'warning',
    showCancelButton: true,
    confirmButtonText: 'Cancel campaign',
    cancelButtonText: 'Keep it'
  }).then(async (result: any) => {
    if (!result.value) return
    try {
      const config = await authConfig()
      await axios.post(`${baseUrl}/email-campaigns/${campaign.id}/cancel`, {}, config)
      proxy.$notify({ type: 'success', message: 'Campaign cancelled', icon: 'tim-icons icon-check-2' })
    } catch (err) {
      swal('Error!', 'The campaign could not be cancelled', 'error')
    } finally {
      getCampaigns(false)
    }
  })
}

async function setArchived(campaign: any, archived: boolean) {
  try {
    const config = await authConfig()
    await axios.post(`${baseUrl}/email-campaigns/${campaign.id}/archive`, { archived }, config)
    proxy.$notify({
      type: 'success',
      message: archived ? 'Campaign archived' : 'Campaign restored',
      icon: 'tim-icons icon-check-2'
    })
  } catch (err) {
    swal('Error!', archived ? 'The campaign could not be archived' : 'The campaign could not be restored', 'error')
  } finally {
    getCampaigns(false)
  }
}

function deletePrompt(campaign: any) {
  swal({
    title: 'Delete this draft?',
    text: `"${campaign.name}" will be permanently removed.`,
    type: 'warning',
    showCancelButton: true,
    confirmButtonText: 'Delete',
    cancelButtonText: 'Keep it'
  }).then(async (result: any) => {
    if (!result.value) return
    try {
      const config = await authConfig()
      await axios.delete(`${baseUrl}/email-campaigns/${campaign.id}`, config)
      proxy.$notify({ type: 'success', message: 'Draft deleted', icon: 'tim-icons icon-check-2' })
    } catch (err) {
      swal('Error!', 'The draft could not be deleted', 'error')
    } finally {
      getCampaigns(false)
    }
  })
}

// FilterBar (saved-views enabled) resolves precedence and drives the first fetch
// via its initial `change` (onFiltersChanged) — no self-fetch here.
onMounted(() => {})

return { __sfc: true,proxy, companyId, companyGroupId, baseUrl, loading, refreshing, campaigns, totalRows, currentPage, perPage, view, activeTab, filterValues, filterSchema, hasActiveFilters, STATUS_COLORS, statusColor, statusLabel, sendTimeDisplay, formatDateTime, authConfig, sendingPollTimer, syncSendingPoll, getCampaigns, switchTab, onFiltersChanged, onPerPageChange, isEditable, openCampaign, createCampaign, editCampaign, viewReport, copyUtmToken, duplicateCampaign, cancelPrompt, setArchived, deletePrompt, BPagination, formatGroupDate, Badge, TabRail }
}

})